import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  getTheme,
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import { useState } from "react";

import { Inventory } from "../../components/ItemReplacement/types/contractItem.interface";

import { useApi } from "@/core/libs/api/react-query/useApi";

export interface InventoryInfo {
  canAttach: boolean;
  reason?: string;
  inventory?: Inventory;
}

interface LinkInventoryModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: (serialNumber: string) => Promise<void>;
  contractItemId?: number;
}

export function LinkInventoryModal({
  isOpen,
  onDismiss,
  onConfirm,
  contractItemId
}: LinkInventoryModalProps): JSX.Element {
  const [serialNumber, setSerialNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [inventoryInfo, setInventoryInfo] = useState<InventoryInfo>(null);
  const { execute } = useApi();
  const theme = getTheme();

  const handleSearch = async () => {
    if (!contractItemId) return;

    const response = await execute<InventoryInfo>({
      url: `contracts/items/${contractItemId}/check-availability`,
      params: { serialNumber },
      setIsFetching: setIsLoading
    });

    setInventoryInfo(response.data);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm(inventoryInfo.inventory.serialNumber);
    handleClose();
  };

  const handleClose = () => {
    setSerialNumber("");
    setInventoryInfo(null);
    setIsLoading(false);
    onDismiss();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void handleSearch();
  };

  const renderInventoryAvailabilityBanner = () => {
    if (!inventoryInfo || isLoading) return null;

    const isAvailable = inventoryInfo.canAttach;
    const messageBarType = isAvailable
      ? MessageBarType.success
      : MessageBarType.error;

    const message = isAvailable
      ? "Inventário disponível"
      : inventoryInfo.reason;

    return (
      <MessageBar
        messageBarType={messageBarType}
        styles={{
          root: { borderRadius: 4, padding: "4px" },
          text: { fontSize: 14 }
        }}
      >
        {message}
      </MessageBar>
    );
  };

  const renderInventoryDetails = () => {
    if (!inventoryInfo?.inventory || isLoading) return null;

    const { inventory } = inventoryInfo;
    const { product, distributionCenter } = inventory;

    return (
      <section
        aria-labelledby="inventory-details-title"
        style={{
          backgroundColor: theme.palette.neutralLighter,
          borderRadius: 4,
          padding: 16
        }}
      >
        <Text
          as="h3"
          variant="mediumPlus"
          styles={{ root: { fontWeight: 600 } }}
        >
          Informações do Inventário
        </Text>

        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <Label>Número de Série:</Label>
          <Text>{inventory.serialNumber}</Text>
        </Stack>

        {product && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Label styles={{ root: { fontWeight: 600 } }}>Produto:</Label>{" "}
            <Text>{product.name}</Text>
          </Stack>
        )}

        {distributionCenter && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Label styles={{ root: { fontWeight: 600 } }}>
              Centro de Distribuição:
            </Label>
            <Text>{distributionCenter.name}</Text>
          </Stack>
        )}
      </section>
    );
  };

  const dialogContentProps = {
    type: DialogType.normal,
    title: "Vincular Inventário"
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleClose}
      dialogContentProps={dialogContentProps}
      minWidth={500}
    >
      <Stack tokens={{ childrenGap: 12 }}>
        <form onSubmit={handleSubmit}>
          <Stack tokens={{ childrenGap: 8 }} horizontal verticalAlign="end">
            <Stack.Item grow>
              <TextField
                label="Número de Série"
                placeholder="Digite o número de série"
                value={serialNumber}
                onChange={(_, value) => {
                  const numericValue = value?.replace(/\D/g, "");
                  setSerialNumber(numericValue);
                }}
                disabled={isLoading}
                aria-describedby="serial-number-hint"
              />
            </Stack.Item>
            <DefaultButton
              type="submit"
              text="Buscar"
              disabled={isLoading || !serialNumber.trim()}
              ariaLabel="Buscar inventário pelo número de série"
            />
          </Stack>
        </form>

        {renderInventoryDetails()}
        {renderInventoryAvailabilityBanner()}

        {isLoading && (
          <Stack
            as="output"
            horizontalAlign="center"
            tokens={{ padding: 16 }}
            aria-live="polite"
          >
            <Spinner size={SpinnerSize.medium} label="Carregando..." />
          </Stack>
        )}
      </Stack>

      <DialogFooter>
        <DefaultButton text="Cancelar" onClick={handleClose} />
        <PrimaryButton
          text="Confirmar"
          onClick={() => void handleConfirm()}
          disabled={!inventoryInfo || isLoading || !inventoryInfo.canAttach}
        />
      </DialogFooter>
    </Dialog>
  );
}

export default LinkInventoryModal;
